import { useChainList } from '../../../../store/chainList';
import { Avatar } from './Avatar';
import { HightLight } from './Highlight';
import { Tooltip } from '../../SearchTooltip';
import { ImageWithDefault } from '@web3/components';
import { TokenListItem } from '../../api';
import { formatNumber, formatNumberPercentage, Chain, isSameAddress } from '@web3/utils';
import BigNumber from 'bignumber.js';
import JupLogo from '../../images/jupiterLogo.svg';
import HotFlag from '../../images/hot_flag.png';
import PumpfunLogo from '../../images/pumpfun.png';

import { searchStorageKey } from '../../../../utils/constants';
import { useLocale } from '../../../../store/i18nMessage';

import clx from 'classnames';

import styles from './index.module.scss';

interface Props {
  searchText: string;
  tokenItem: TokenListItem;
  onChangeHistory: (history: string[]) => void;
}

export const ethAddress = 'eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

export const getDexLink = (item: TokenListItem, chain: Chain, locale) => {
  const chainCode = chain?.chainCode;

  if (!chainCode) {
    return `/${locale}/web3/dex/`;
  }
  const defaultLink = `/${locale}/web3/dex/${chainCode?.toLowerCase()}/`;
  const isSame = isSameAddress(item?.address, ethAddress);
  return isSame ? `${defaultLink}?utm_content=web3Menu_search` : `${defaultLink + item.address}?utm_content=web3Menu_search`;
};

export const valueChangeClassName = (index: string | number) => {
  let numStr = String(index);
  if (numStr.endsWith('%')) {
    numStr = numStr.replace('%', '');
  }
  const num = BigNumber(numStr);
  if (num.eq(0)) {
    return 'text-no-change';
  } else if (num.gt(0)) {
    return 'text-go-up';
  } else {
    return 'text-go-down';
  }
};

export const TableItem = (props: Props) => {
  const { searchText, tokenItem, onChangeHistory } = props;
  const { mixedList } = useChainList();
  const { locale } = useLocale();

  const chain = mixedList.filter((item) => +item.chainId === +tokenItem.chainId)[0];

  const tokenTagsImgMap = {
    verified: {
      src: JupLogo,
      tips: 'Jupiter Verified',
      alt: 'jupiterLogo',
    },
    pumpFun: {
      src: PumpfunLogo,
      tips: 'PumpFun',
      alt: 'pumpfunLogo',
    },
    hot: {
      src: HotFlag,
      tips: 'Hot',
      alt: 'hotLogo',
    },
  };

  const formatTokenAddress = (address: string) => {
    if (!address) return '';
    if (address?.length < 4) return address;

    return address.replace(/.*(.{4})$/, '*$1');
  };

  const renderPrice = () => {
    const priceNum = BigNumber(tokenItem.price);
    const conditional = priceNum.lt(0.00001) || priceNum.gt(1000000);
    const tipContent = formatNumber(tokenItem.price, {
      kmb: false,
      decimal: 4,
      roundMode: 7,
      collapseZero: false,
    });
    return (
      <Tooltip content={`$${tipContent}`} conditional={conditional}>
        <span
          className={clx(styles.price, {
            [styles.underline]: conditional,
          })}
        >
          {`$${formatNumber(tokenItem.price, {
            minKmbUnit: 'M',
            maxKmbUnit: 'T',
            decimal: 4,
            kmbDecimal: 2,
            roundMode: 7,
          })}`}
        </span>
      </Tooltip>
    );
  };

  const saveSearchHistory = (text: string) => {
    if (!text) return;
    const li = localStorage.getItem(searchStorageKey) || '[]';
    try {
      const re = JSON.parse(li) || [];
      // state.historyList = re;

      const findIndex = re.findIndex((it) => it === text);
      if (findIndex >= 0) {
        re.splice(findIndex, 1);
      }
      if (re.length >= 5) {
        re.pop();
      }
      re.unshift(text);
      localStorage.setItem(searchStorageKey, JSON.stringify(re));
      onChangeHistory(re || []);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div
      className={styles.container}
      onClick={() => {
        if (searchText) {
          saveSearchHistory(searchText);
        }

        const link = getDexLink(tokenItem, chain, locale);
        window.open(link, '_blank');
      }}
    >
      <div className={styles.left}>
        <div className={styles.content}>
          <div className={styles.icons}>
            <Avatar className={styles.tokenIcon} src={tokenItem.icon} size="32" name={tokenItem.tokenName} />
            <ImageWithDefault
              width={16}
              height={16}
              resize={{
                width: 16,
                height: 16,
              }}
              className={styles.chainIcon}
              src={chain?.iconNightUrl}
            />
          </div>
          <div className={styles.token}>
            <div className={styles.tokenInfo}>
              <span className={styles.name}>
                <HightLight text={tokenItem.tokenName} keywords={searchText ?? ''} />
              </span>
              {tokenItem.address && tokenItem.address !== ethAddress ? (
                <span className={styles.address}>{`(${formatTokenAddress(tokenItem.address)})`}</span>
              ) : null}
              <div className={styles.tags}>
                {tokenItem?.tokenTags?.map((tag, index) => {
                  const tagItem = tokenTagsImgMap[tag];
                  if (tagItem) {
                    return (
                      <Tooltip key={index} content={tagItem.tips}>
                        <div>
                          <img src={tagItem.src} alt={tagItem.alt} width={12} height={12} />
                        </div>
                      </Tooltip>
                    );
                  }

                  return null;
                })}
              </div>
            </div>
            <div className={styles.data}>
              <div className={styles.item}>
                <span className={styles.prefix}>Vol</span>
                <span className={styles.num}>{`$${formatNumber(tokenItem.volume)}`}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.prefix}>LP</span>
                <span className={styles.num}>{`$${formatNumber(tokenItem.liquidity)}`}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {renderPrice()}
        <span className={clx(valueChangeClassName(tokenItem.priceChange), styles.change)}>
          {formatNumberPercentage(tokenItem.priceChange, 2, { scale: 1 })}
        </span>
      </div>
    </div>
  );
};
